import { RESERVED_FEED_COLUMNS } from '@features/scoreSpec/specSchema';
import { scoreNameForFeed } from './specForFeed';

// Which payload columns a feed carries, read off the score spec that published it.
//
// This is the answer to a question the response cannot answer in time. A score spec's payload keys
// are its declared `extra_columns` — arbitrary names, different per spec — and the `columns` request
// field is an allowlist over exactly those names. So a feed nobody has read yet can only be asked for
// names guessed from a fixed list, and no fixed list can contain them. The spec, on the other hand,
// is knowable from the feed id alone (specForFeed.js) and one cheap GET, before the read goes out.
//
// TWO SCOPES, and the distinction decides which feed carries which column:
//   * `output_extraction.extra_columns` is BROADCAST — every feed the spec publishes carries it.
//   * `output_extraction.scores[].extra_columns` is that one score's, so it lands only on that
//     score's own feed.
// A multi-score spec publishes `score_spec_<id>__<name>` per score, so the feed id names which score
// to take the scoped set from. Taking every score's would ask a feed for a sibling's column, which
// under the additive contract is a 422 on the whole read.
//
// Same shape PreviewDock.js:165-178 walks for the preview's declared paths. Kept separate rather than
// shared: that one is building labels for a human to read and tolerates listing every score's extras
// at once, which is the exact thing this must not do.

const isObject = (value) => !!value && typeof value === 'object' && !Array.isArray(value);

const keysOf = (value) => (isObject(value) ? Object.keys(value) : []);

// A spec-level extra_columns name that collides with a reserved row column is TOLERATED at load and
// then silently dropped from the payload at view time (specSchema.js:262-265). Asking for one would
// therefore request a column that can never arrive — and for the four that are also in
// ALWAYS_COLUMN_FIELDS, naming it is a documented no-op anyway. Dropped here so the keep-list only
// ever contains names that can really come back.
const isRequestable = (name) => typeof name === 'string' && !!name && !RESERVED_FEED_COLUMNS.includes(name);

// The payload column names for one feed, from that feed's spec definition (`spec.spec_json`).
// Sorted so a keep-list is stable across reads and a test can assert one.
export const payloadNamesFromSpec = (definition, feedId) => {
  const output = isObject(definition) && isObject(definition.output_extraction) ? definition.output_extraction : null;
  if (!output) return [];

  const broadcast = keysOf(output.extra_columns);

  // Null for a single-score spec, whose feed id is bare — and such a spec has no `scores[]` to scope
  // anything to, so broadcast is the whole answer.
  const scoreName = scoreNameForFeed(feedId);
  const scores = Array.isArray(output.scores) ? output.scores : [];
  // No match is not the same as no scoped columns, but it produces the same list, and that is the
  // right direction: a feed id naming a score this spec doesn't have is a naming rule that has drifted
  // (the same drift specForFeed.js fails closed on), and asking for columns on the strength of it
  // would turn a wrong guess into a 422 that costs the feed its enrichment for the session.
  const scoped = scoreName
    ? keysOf((scores.find((score) => isObject(score) && score.name === scoreName) || {}).extra_columns)
    : [];

  return Array.from(new Set([...broadcast, ...scoped].filter(isRequestable))).sort();
};
